import React, { useRef, useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { Tab, Tabs } from '@mui/material';

import { Button, TabPanel } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { SelectSingle, FormikControl, DateRangeSelector, checkResponses } from '../../../../components/form';
import { formatDateApi } from '../../../../components/functions';
import { FlagModal } from '../../../../components/modal';
import { CSVExport } from '../../../../components/export';
import { KanbanBoard } from '../../../../components/kanban';
import FlagsTable from './FlagsTable';
import { Socket } from '../../../../components/ws';

import {
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchAdminUsersAll,
    fetchStudentsAll,
    fetchFlagsDaterange,
    updateFlagKanbanStatus,
    updateFlagsKanbanOrder
} from '../../../../actions';

const pageTitle = 'Flag Manager';

// Detect portal from domain
const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
const isReadingPlex = hostname.includes('readingplex');

const filterTypeOptions = [
    { value: 'all', label: 'All' },
    { value: 'studentName', label: 'Student Name' },
    { value: 'instructorName', label: 'Instructor Name' },
    { value: 'center', label: 'Center' },
    { value: 'notes', label: 'Notes' },
];

const start = new Date();
start.setMonth(start.getMonth() - 2);
const startApi = formatDateApi(start);
const endApi = formatDateApi(new Date());

function FlagManager(props){
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();
    
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [drsValid, setDrsValid] = useState(true); // dateRangeSelector
    const [apiError, setApiError] = useState(false);

    // DATA
    const [flags, setFlags] = useState([]);
    const [students, setStudents] = useState([]);
    const [instructors, setInstructors] = useState([]);

    const [pendingFlagsFiltered, setPendingFlagsFiltered] = useState([]);
    const [completedFlagsFiltered, setCompletedFlagsFiltered] = useState([]);
    const [centers, setCenters] = useState([{ value: -1, label: 'Loading centers...' }]);
    const [activeTabKey, setActiveTabKey] = useState(0);
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'kanban' (ReadingPlex only)

    const [modalMode, setModalMode] = useState(null);
    const [selectedFlag, setSelectedFlag] = useState(null);

    const { fetchFlagsDaterange, fetchMpCentersAll, fetchRpCentersAll, fetchAdminUsersAll, fetchStudentsAll, updateFlagKanbanStatus, updateFlagsKanbanOrder } = props;

    const filterFlags = useCallback((unfilteredFlags, filterType, filterQuery, currentViewMode = viewMode) => {
        filterQuery = filterQuery.replace(/ /g, '').toLowerCase();
        const checkStudent = (flag) => flag.studentName.toLowerCase().replace(/ /g, '').includes(filterQuery);
        const checkInstructors = (flag) => {
            return (
                flag.createdByName.replace(/ /g, '').toLowerCase().includes(filterQuery) ||
                flag.updatedByName.replace(/ /g, '').toLowerCase().includes(filterQuery)
            );
        }
        const checkNotes = (flag) => {
            return (
                flag.notes.replace(/ /g, '').toLowerCase().includes(filterQuery) || 
                flag.admin_notes.replace(/ /g, '').toLowerCase().includes(filterQuery)
            );
        }
        const checkCenters = (flag) => flag.centerName.replace(/ /g, '').toLowerCase().includes(filterQuery);

        const newFlags = unfilteredFlags.filter(f => {
            switch(filterType.value){
                case 'all':
                    return checkStudent(f) || checkInstructors(f) || checkNotes(f) || checkCenters(f);
                case 'studentName':
                    return checkStudent(f);
                case 'instructorName':
                    return checkInstructors(f);
                case 'center':
                    return checkCenters(f);
                case 'notes':
                    return checkNotes(f);
                default:
                    return false;
            }
        });

        // Sort by type and insert into proper state array
        // For Kanban view, pass all flags that have kanban_status
        if (currentViewMode === 'kanban') {
            const kanbanFlags = newFlags.filter(f => f.kanban_status);
            setPendingFlagsFiltered(kanbanFlags); // Use pendingFlagsFiltered to hold all kanban tasks
            setCompletedFlagsFiltered([]); // Clear completed for kanban view as all are in pendingFlagsFiltered
        } else {
            // For table view, split by status: To Do/In Progress/Review -> Pending tab, Done -> Completed tab
            const newPendingFlags = newFlags.filter(f => {
                const status = f.status || f.kanban_status || 'To Do';
                return status !== 'Done';
            });
            const newCompletedFlags = newFlags.filter(f => {
                const status = f.status || f.kanban_status || 'To Do';
                return status === 'Done';
            });
            setPendingFlagsFiltered(newPendingFlags);
            setCompletedFlagsFiltered(newCompletedFlags);
        }
    }, [viewMode]);
    
    // Re-filter flags when viewMode changes
    useEffect(() => {
        if (flags.length > 0) {
            const { filterQuery = '', filterType = { value: 'all' } } = (formRef.current && formRef.current.values) || {};
            filterFlags(flags, filterType, filterQuery, viewMode);
        }
    }, [viewMode, flags, filterFlags]);

    // Safety: Kanban is ReadingPlex-only. Force MathPlex into table view if somehow toggled.
    useEffect(() => {
        if (!isReadingPlex && viewMode !== 'table') {
            setViewMode('table');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);
    const refreshData = useCallback((newStudents = students, newInstructors = instructors, newCenters = centers) => {
        (async function refresh(){
            if(loading || !drsValid || !formRef.current || !formRef.current.values) return;
            if(mounted.current) setLoading(true);
    
            const { filterQuery, filterType, startDate, endDate } = formRef.current.values;
            
            const instructorMap = {};
            newInstructors.forEach(i => instructorMap[i.id] = `${i.first_name} ${i.last_name}`);
    
            const studentMap = {};
            newStudents.forEach(s => studentMap[s.user_id] = `${s.first_name} ${s.last_name}`);

            // For ReadingPlex, also create a combined user map (students + employees)
            const userMap = {};
            if (isReadingPlex) {
                // Add students to user map
                newStudents.forEach(s => userMap[s.user_id] = `${s.first_name} ${s.last_name} (Student)`);
                // Add employees/instructors to user map
                newInstructors.forEach(i => userMap[i.id] = `${i.first_name} ${i.last_name} (${i.rp_permissions || 'Employee'})`);
            }

            const centerMap = {};
            newCenters.forEach(c => centerMap[parseInt(c.id)] = c.name);
    
            const flagsRes = await fetchFlagsDaterange({
                startDate: startDate,
                endDate: endDate
            });
            const isApiError = checkResponses(flagsRes);
            if(isApiError){
                if(mounted.current){
                    setApiError('Error fetching data from the server. Please try again later.');
                    setLoading(false);
                }
                return;
            } else setApiError(false);

            const newFlags = flagsRes.data || [];
            const flagsAppended = newFlags.map(f => {
                // ReadingPlex uses 'user' field, MathPlex uses 'student' field
                const userField = isReadingPlex ? (f.user || f.student) : (f.student || f.user);
                
                if (isReadingPlex) {
                    // For ReadingPlex, check both student map and combined user map (for employees)
                    // Handle case where userField might be space-separated IDs (e.g., "12 34")
                    let userName = 'Unknown user';
                    if (userField) {
                        // Try exact match first
                        userName = userMap[userField] || studentMap[userField];
                        
                        // If not found and contains spaces, try splitting and finding first match
                        if (!userName && typeof userField === 'string' && userField.includes(' ')) {
                            const ids = userField.trim().split(/\s+/);
                            for (const id of ids) {
                                const found = userMap[id] || studentMap[id];
                                if (found) {
                                    userName = found;
                                    break;
                                }
                            }
                        }
                        
                        // If still not found, show the raw value
                        if (!userName) {
                            userName = `Unknown user (UID: ${userField})`;
                        }
                    }
                    f.userName = userName;
                    f.studentName = userName; // Also set studentName for table view compatibility
                } else {
                    // For MathPlex, use studentName
                    const relStudentName = userField ? studentMap[userField] : null;
                    f.studentName = relStudentName || (userField ? `Unknown student (UID: ${userField})` : 'Unknown student');
                }

                const createdByInstructorName = instructorMap[f.created_by];
                f.createdByName = createdByInstructorName || `Unknown admin user (ID: ${f.created_by})`;

                const updatedByInstructorName = f.updated_by ? instructorMap[f.updated_by] : 'None';
                f.updatedByName = updatedByInstructorName || `Unknown admin user (ID: ${f.updated_by})`;

                f.centerName = centerMap[parseInt(f.center)] || `Unknown center (ID: ${f.center})`;

                // Map assigned employees IDs to names
                if (f.assigned_employees) {
                    try {
                        const empIds = typeof f.assigned_employees === 'string' 
                            ? JSON.parse(f.assigned_employees) 
                            : f.assigned_employees;
                        if (Array.isArray(empIds)) {
                            f.assignedEmployeeNames = empIds.map(id => {
                                const name = instructorMap[id];
                                return name || `Unknown (ID: ${id})`;
                            });
                        }
                    } catch (e) {
                        console.error('[FlagManager] Error parsing assigned_employees:', e);
                        f.assignedEmployeeNames = [];
                    }
                } else {
                    f.assignedEmployeeNames = [];
                }

                // Map students IDs to names
                if (f.students) {
                    try {
                        const studentIds = typeof f.students === 'string' 
                            ? JSON.parse(f.students) 
                            : f.students;
                        if (Array.isArray(studentIds)) {
                            f.studentNames = studentIds.map(id => {
                                const name = studentMap[id];
                                return name || `Unknown (ID: ${id})`;
                            });
                        }
                    } catch (e) {
                        console.error('[FlagManager] Error parsing students:', e);
                        f.studentNames = [];
                    }
                } else {
                    f.studentNames = [];
                }

                // Sync status and kanban_status - they should be the same now (To Do, In Progress, Review, Done)
                // Map old status values to new ones
                if (isReadingPlex) {
                    if (f.status === 'Pending') {
                        f.status = f.kanban_status || 'To Do';
                        f.kanban_status = f.kanban_status || 'To Do';
                    } else if (f.status === 'Completed') {
                        f.status = 'Done';
                        f.kanban_status = 'Done';
                    } else if (!f.status && f.kanban_status) {
                        f.status = f.kanban_status;
                    } else if (f.status && !f.kanban_status) {
                        f.kanban_status = f.status;
                    } else if (!f.status && !f.kanban_status) {
                        f.status = 'To Do';
                        f.kanban_status = 'To Do';
                    }
                }

                return f;
            });
    
            if(mounted.current){
                setFlags(flagsAppended);
                filterFlags(flagsAppended, filterType, filterQuery, viewMode);
                setLoading(false);
            }
        })();
    }, [loading, setLoading, filterFlags, formRef, drsValid, instructors, centers, students, fetchFlagsDaterange, viewMode]);
    useEffect(() => {
        async function init(){
            if(mounted.current) setLoading(true);
            // Detect portal from domain - use ReadingPlex centers on ReadingPlex domain
            const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
            const isReadingPlex = hostname.includes('readingplex');
            const centersRes = isReadingPlex ? await fetchRpCentersAll() : await fetchMpCentersAll();
            const studentsRes = await fetchStudentsAll();
            const instructorsRes = await fetchAdminUsersAll();
            const isApiError = checkResponses(centersRes, studentsRes, instructorsRes);
            if(isApiError){
                if(mounted.current){
                    setApiError('Error fetching data from the server. Please refresh the page or try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
                return;
            }
            
            const newCenters = Array.isArray(centersRes.data) ? centersRes.data : [];
            const newStudents = Array.isArray(studentsRes.data) ? studentsRes.data : [];
            const newInstructors = Array.isArray(instructorsRes.data) ? instructorsRes.data : [];

            if(mounted.current){
                setStudents(newStudents);
                setCenters(newCenters);
                setInstructors(newInstructors);
                setLoading(false);
                setHasLoaded(true);
                refreshData(newStudents, newInstructors, newCenters);
            }
        }
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmitCallback = useCallback((changes = false) => {
        if(mounted.current){
            setModalMode(null);
            setSelectedFlag(null);
            if(changes) refreshData();
        }
    }, [mounted, refreshData]);

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay/>}
            { modalMode &&
                <FlagModal
                    mode={modalMode}
                    selectedFlag={selectedFlag}
                    onSubmitCallback={onSubmitCallback}
                />
            }
            {hasLoaded &&
                <div className="card">
                    <Formik
                        enableReinitialize
                        initialValues={{
                            startDate: startApi,
                            endDate: endApi,
                            filterType: { value: 'all', label: 'All' },
                            filterQuery: '',
                            activeKey: 'pending'
                        }}
                        innerRef={formRef}
                        onSubmit={() => refreshData(students, instructors)}
                    >
                        {formik => (
                            <form onSubmit={formik.handleSubmit}>
                                <h2>Flags</h2>

                                <br/>
                                
                                <div className="flex flex-row gap-x-4 items-center">
                                    <div className="grid grid-cols-1 gap-y-2">
                                        <DateRangeSelector
                                            id="flag-manager-drs"
                                            startName="startDate"
                                            endName="endDate"
                                            startLabel="Start Date"
                                            endLabel="End Date"
                                            startValue={formik.values.startDate}
                                            endValue={formik.values.endDate}
                                            defaultValid={true}
                                            onStartChange={formik.handleChange}
                                            onEndChange={formik.handleChange}
                                            onChangeValidation={setDrsValid}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 gap-y-2">
                                        <Button
                                            color="lte-mpTeal"
                                            onClick={formik.handleSubmit}
                                        >
                                            Search
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-y-2">
                                        <Button
                                            color="lte-mpLBlue"
                                            onClick={() => setModalMode('create')}
                                        >
                                            + Add Flag
                                        </Button>
                                    </div>
                                    <div className="flex ml-auto">
                                        <TooltipWrapper
                                            tooltipText={
                                                <div>
                                                    <div>
                                                        What gets exported?
                                                    </div>
                                                    <br/>
                                                    <div>
                                                        All flags that are currently filtered
                                                        ({formik.values.activeKey === 'pending' ?
                                                            pendingFlagsFiltered.length : completedFlagsFiltered.length} items).
                                                        This depends on the tab that is selected.
                                                    </div>
                                                </div>
                                            }
                                        >
                                            <CSVExport
                                                title="Flags"
                                                label="Export Flags to CSV"
                                                data={formik.values.activeKey === 'pending' ? pendingFlagsFiltered : completedFlagsFiltered}
                                            />
                                        </TooltipWrapper>
                                    </div>
                                </div>
                                
                                <br/>
                                
                                { apiError ? <div className="text-mpLRed">{apiError}</div> : 
                                    <>
                                        <hr/>
                                        <br/>

                                        <h2>Filter Results</h2>
                                        <br/>
                                        <div className="flex flex-row gap-x-4 items-center">
                                            <div className="grid grid-cols-1 gap-y-2 w-1/3">
                                                <SelectSingle
                                                    id="flag-manager-searchType"
                                                    name="filterType"
                                                    label="Filter by"
                                                    value={formik.values.filterType}
                                                    onChange={(e) => {
                                                        formik.handleChange(e);
                                                        filterFlags(flags, e.target.value, formik.values.filterQuery, viewMode);
                                                    }}
                                                    options={filterTypeOptions}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 gap-y-2 w-1/3">
                                                <FormikControl
                                                    id="flag-manager-searchQuery"
                                                    name="filterQuery"
                                                    placeholder="Enter a filter query..."
                                                    value={formik.values.filterQuery}
                                                    onChange={(e) => {
                                                        formik.handleChange(e);
                                                        filterFlags(flags, formik.values.filterType, e.target.value, viewMode);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <br/>
                                        {/* View Toggle */}
                                        <div className="flex flex-row items-center justify-between mb-4">
                                            <div className="flex flex-row gap-2">
                                                <Button
                                                    color={viewMode === 'table' ? 'hol-mpLBlue' : 'lte-mpLBlue'}
                                                    onClick={() => setViewMode('table')}
                                                >
                                                    <i className="fas fa-table mr-2"></i> Table View
                                                </Button>
                                                {isReadingPlex && (
                                                    <Button
                                                        color={viewMode === 'kanban' ? 'hol-mpLBlue' : 'lte-mpLBlue'}
                                                        onClick={() => setViewMode('kanban')}
                                                    >
                                                        <i className="fas fa-columns mr-2"></i> Kanban View
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {isReadingPlex && viewMode === 'kanban' ? (
                                            // Kanban Board View - use pendingFlagsFiltered which contains all kanban flags
                                            <KanbanBoard
                                                tasks={pendingFlagsFiltered}
                                                onTaskClick={(task) => {
                                                    setModalMode('edit');
                                                    setSelectedFlag(task);
                                                }}
                                                onStatusChange={async (task, newStatus) => {
                                                    try {
                                                        const response = await updateFlagKanbanStatus({
                                                            id: task.id,
                                                            kanbanStatus: newStatus
                                                        });
                                                        if (response?.status === 200) {
                                                            refreshData();
                                                        } else {
                                                            console.error('[Kanban] Failed to update status:', response);
                                                            alert('Failed to update task status. Please try again.');
                                                        }
                                                    } catch (error) {
                                                        console.error('[Kanban] Error updating status:', error);
                                                        alert('Error updating task status. Please try again.');
                                                    }
                                                }}
                                                onReorder={async (updates) => {
                                                    try {
                                                        const response = await updateFlagsKanbanOrder({ updates });
                                                        if (response?.status !== 200) {
                                                            console.error('[Kanban] Failed to update order:', response);
                                                        }
                                                    } catch (error) {
                                                        console.error('[Kanban] Error updating order:', error);
                                                    }
                                                }}
                                                loading={loading}
                                            />
                                        ) : (
                                            // Table View
                                            <>
                                                <Tabs
                                                    id="contracts-payments"
                                                    value={activeTabKey}
                                                    onChange={(e, newValue) => setActiveTabKey(newValue)}
                                                >
                                                    <Tab label={`Pending (${pendingFlagsFiltered.length})`}/>
                                                    <Tab label={`Completed (${completedFlagsFiltered.length})`}/>
                                                </Tabs>
                                                <TabPanel activeKey={activeTabKey} index={0}>
                                                    <FlagsTable
                                                        refreshData={refreshData}
                                                        flags={pendingFlagsFiltered}
                                                        tableName="flags-pending"
                                                    />
                                                </TabPanel>
                                                <TabPanel activeKey={activeTabKey} index={1}>
                                                    <FlagsTable
                                                        refreshData={refreshData}
                                                        flags={completedFlagsFiltered}
                                                        tableName="flags-completed"
                                                    />
                                                </TabPanel>
                                            </>
                                        )}
                                    </>
                                }
                                
                            </form>
                        )}
                    </Formik>
                </div>
            }
            <Socket
                refreshData={refreshData}
                page={pageTitle}
                setVersion={props.setVersion}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        auth: state.auth
    };
}

export default connect(mapStateToProps, {
    fetchMpCentersAll,
    fetchRpCentersAll,
    fetchAdminUsersAll,
    fetchStudentsAll,
    fetchFlagsDaterange,
    updateFlagKanbanStatus,
    updateFlagsKanbanOrder
})(FlagManager);